// Monaco-backed code editor wrapper.
//
// Auto-detects language from the file path and follows the MUI light/dark theme.

import React, { useMemo } from "react";
import { Box, useTheme } from "@mui/material";
import Monaco from "@monaco-editor/react";

export interface CodeEditorProps {
  path: string;
  value: string;
  onChange: (next: string) => void;
  /** Cmd/Ctrl-S handler. */
  onSave?: () => void;
  readOnly?: boolean;
}

function languageFor(path: string): string {
  if (path.endsWith(".tsx")) return "typescript";
  if (path.endsWith(".ts")) return "typescript";
  if (path.endsWith(".jsx")) return "javascript";
  if (path.endsWith(".js")) return "javascript";
  if (path.endsWith(".json")) return "json";
  if (path.endsWith(".css")) return "css";
  if (path.endsWith(".html")) return "html";
  if (path.endsWith(".md")) return "markdown";
  return "plaintext";
}

const CodeEditor: React.FC<CodeEditorProps> = ({
  path,
  value,
  onChange,
  onSave,
  readOnly,
}) => {
  const muiTheme = useTheme();
  const monacoTheme = muiTheme.palette.mode === "dark" ? "vs-dark" : "light";
  const language = useMemo(() => languageFor(path), [path]);

  return (
    <Box sx={{ height: "100%", width: "100%" }}>
      <Monaco
        height="100%"
        path={path}
        language={language}
        value={value}
        theme={monacoTheme}
        onChange={(v) => onChange(v ?? "")}
        options={{
          readOnly: !!readOnly,
          minimap: { enabled: false },
          fontFamily:
            "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
          fontSize: 13,
          lineNumbers: "on",
          renderLineHighlight: "line",
          scrollBeyondLastLine: false,
          tabSize: 2,
          insertSpaces: true,
          automaticLayout: true,
          wordWrap: "on",
          quickSuggestions: { other: true, comments: false, strings: true },
          stickyScroll: { enabled: true },
        }}
        beforeMount={(monaco) => {
          // Don't show TS error squiggles for un-resolvable imports — the
          // runtime resolves them via importmap and per-file blob URLs, so
          // semantic validation in the editor would only produce false alarms.
          monaco.languages.typescript.typescriptDefaults.setDiagnosticsOptions({
            noSemanticValidation: true,
            noSyntaxValidation: false,
          });
          monaco.languages.typescript.typescriptDefaults.setCompilerOptions({
            jsx: monaco.languages.typescript.JsxEmit.React,
            target: monaco.languages.typescript.ScriptTarget.ES2020,
            allowNonTsExtensions: true,
            moduleResolution:
              monaco.languages.typescript.ModuleResolutionKind.NodeJs,
            module: monaco.languages.typescript.ModuleKind.ESNext,
            esModuleInterop: true,
            isolatedModules: true,
          });
        }}
        onMount={(editor, monaco) => {
          if (onSave) {
            editor.addCommand(
              monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS,
              () => onSave(),
            );
          }
        }}
      />
    </Box>
  );
};

export default CodeEditor;
